import React, { FC, memo, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Drawer } from 'shared/ui/Drawer';
import { showNotification } from 'app/providers/NotificationsProvider';
import { getContractNoteDrawerState } from '../model/selectors/getContractNoteDrawerState';
import { TextArea } from 'shared/ui/TextArea';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Paragraph } from 'shared/ui/Paragraph';
import { contractPageActions } from '../model/slice/contractsPageSlice';
import { useUpdateContractMutation } from 'entities/Contract';
import { getLoggedUserData, getUserName } from 'entities/User';

export const ContractNoteDrawer: FC = memo(() => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation(['contracts', 'common']);

  const contract = useAppSelector(getContractNoteDrawerState);

  const loggedUserData = useAppSelector(getLoggedUserData);

  const [note, setNote] = useState<Nullable<string>>(null);

  const [updateContract, { isLoading }] = useUpdateContractMutation();

  const closeBoxFormDrawer = useCallback((): void => {
    dispatch(contractPageActions.setOpenContractNoteDrawer(null));
  }, [dispatch]);

  const handleChangeNote = useCallback((note: string) => {
    setNote(note);
  }, []);

  const handleAddLeadNote = useCallback(async (): Promise<void> => {
    try {
      if (contract && note) {
        const adminNoteString = `\n${getUserName(loggedUserData)}${t('note:')}\n${note}`;
        await updateContract({ contractId: contract.contractId, params: { notes: adminNoteString } }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Contract note added successfully'));

        closeBoxFormDrawer();

        setNote(null);
      }
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when adding contract note'));
      console.log('error', error);
    }
  }, [closeBoxFormDrawer, contract, loggedUserData, note, t, updateContract]);

  const paragraphs = contract?.notes?.split('\n');

  const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
    <p key={index} className="font-normal !mb-1">
      {paragraph}
    </p>
  ));

  return (
    <Drawer width={450} open={Boolean(contract)} onClose={closeBoxFormDrawer}>
      <div className="flex flex-col px-10 py-4">
        <div className="flex justify-between items-center pt-4 pb-10">
          <div className="font-semibold text-3xl">{t('Notes')}</div>
          <div className="text-primaryLight font-normal cursor-pointer" onClick={closeBoxFormDrawer}>
            {t('Close')}
          </div>
        </div>

        <div className="flex flex-col gap-8">
          <div>
            <div className="mb-5">
              <div className="mb-2">{t('Note')}</div>
              <TextArea placeholder={t('Add your note...')} rows={4} bordered value={note} onChange={handleChangeNote} />
            </div>
            <Button onClick={handleAddLeadNote} isLoading={isLoading} isDisabled={!contract || !note}>
              {t('Add Note')}
            </Button>
          </div>

          <div>
            <div className="mb-2">{t('Note history')}:</div>
            <Paragraph expandable={false}>{paragraphElements}</Paragraph>
          </div>
        </div>
      </div>
    </Drawer>
  );
});
